import React, { useState, useEffect } from 'react';
import { useDecisionContext } from '../context/DecisionContext';

const QuestionForm: React.FC = () => {
  const { 
    generateDecisions, 
    loading, 
    apiSettings, 
    streamingContent, 
    error, 
    clearError, 
    progress 
  } = useDecisionContext();
  
  const [question, setQuestion] = useState('');
  const [showError, setShowError] = useState(false);

  // 当错误变化时更新错误显示状态
  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  // 清除错误提示
  const handleDismissError = () => {
    setShowError(false);
    clearError();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (question.trim() && apiSettings.apiKey) {
      generateDecisions(question.trim());
    }
  };

  return (
    <div className="mb-8 w-full max-w-3xl">
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">输入您的决策问题</h2>
        
        {showError && error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded animate-fadeIn">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error.message}</h3>
                {error.details && (
                  <p className="mt-1 text-sm text-red-700">{error.details}</p>
                )}
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={handleDismissError}
                    className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100"
                  >
                    <span className="sr-only">关闭</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="例如：我应该接受这份工作offer吗？"
              rows={3}
              className="w-full px-3 py-2 placeholder-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              disabled={loading}
            />
          </div>
          
          <div className="flex flex-col">
            <p className="text-sm text-gray-500 mb-2">
              {!apiSettings.apiKey ? (
                <span className="text-red-500">请先在设置中配置您的API密钥</span>
              ) : (
                '点击分析按钮生成决策选项'
              )}
            </p>
            
            {loading && (
              <div className="mb-4">
                <div className="flex items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">正在分析...</span>
                  <span className="ml-2 text-xs text-gray-500">{progress ? `${progress}%` : ''}</span>
                </div>
                <div className="w-full h-2 relative overflow-hidden bg-gray-200 rounded-full">
                  <div className="animate-pulse absolute top-0 left-0 h-full bg-indigo-600 rounded-full w-full opacity-70"></div>
                  <div 
                    className="absolute top-0 left-0 h-full bg-indigo-600 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !question.trim() || !apiSettings.apiKey}
              className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
                loading || !question.trim() || !apiSettings.apiKey
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              }`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  分析中...
                </span>
              ) : (
                '分析决策'
              )}
            </button>
          </div>
        </form>
        
        {loading && streamingContent && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200 max-h-60 overflow-y-auto animate-fadeIn">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap">{streamingContent}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionForm; 